<script>
  import { onMount } from 'svelte';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate } from '@fortawesome/pro-light-svg-icons';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import SiteDetails from '$lib/SiteDetails.svelte';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';

  const API_BASE_URL = '/api/proxy';

  let rows = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let selectedType = 'ALL';
  let period = 'month'; // day | week | month
  let isExporting = false;
  let inRange = true;
  const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  onMount(async () => {
    if ($selectedStudy) {
      fetchCounts(1);
    } else {
      isLoading = false;
    }
  });

  // Refetch when study, status, or period changes
  $: (async () => {
    if ($selectedStudy) {
      await fetchCounts(1);
    }
  })();

  async function fetchCounts(page = 1) {
    try {
      isLoading = true;
      let url = `${API_BASE_URL}/glucoseReadings/studyCounts?page=${page}&pageSize=${limit}&studyGUID=${$selectedStudy._id}&subjectStatus=${selectedType}&period=${period}`;

      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }

      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }

      url += `&inRange=${inRange}`;

      const res = await fetch(url);
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch counts: ${errorText}`);
      }

      const responseData = await res.json();
      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;
      rows = responseData.data || [];
    } catch (err) {
      console.error('Error fetching counts:', err);
      rows = [];
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    fetchCounts(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchCounts(newPage);
    }
  }

  async function fetchAllCounts() {
    try {
      let url = `${API_BASE_URL}/glucoseReadings/studyCounts?page=1&pageSize=10000&studyGUID=${$selectedStudy._id}&subjectStatus=${selectedType}&period=${period}&inRange=${inRange}`;
      if ($selectedParticipant) {
        url += `&subjectGUID=${$selectedParticipant._id}`;
      }
      if ($selectedSite) {
        url += `&siteGUID=${$selectedSite._id}`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch all counts');
      const result = await res.json();
      return result.data || [];
    } catch (e) {
      console.error('Error fetching all counts:', e);
      return [];
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;
      const all = await fetchAllCounts();

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        reportType: 'CGM_Counts'
      });

      const formatted = all.map((r) => {
        const label = formatPeriodLabel(period, r.periodStart, r.timezone);
        const periodRange = formatPeriodRangeLocal(period, r.periodStart);
        const compliance = computeCompliance(r.count, period, r.periodStart, r.timezone);
        return {
          Protocol: demoMode ? 'DEMO-0001' : ($selectedStudy?.protocolNumber || ''),
          Site: r.siteId || '',
          Subject: r.subjectId || '',
          Status: r.subjectStatus || '',
          Period: label,
          'Period Start': periodRange.split('\n')[0],
          'Period End': periodRange.split('\n')[1] || '',
          'From (Local)': r.minRead ? new Date(r.minRead).toLocaleString('en-US') : '',
          'To (Local)': r.maxRead ? new Date(r.maxRead).toLocaleString('en-US') : '',
          Count: r.count || 0,
          Compliance: compliance,
          Timezone: r.timezone || ''
        };
      });

      await exportToExcel(formatted, filename);
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      isExporting = false;
    }
  }

  function toLocalDate(dateInput, tz) {
    const d = new Date(dateInput);
    const localStr = d.toLocaleString('en-US', { timeZone: tz || 'UTC' });
    return new Date(localStr);
  }

  function formatPeriodLabel(p, dateInput, tz) {
    if (!dateInput) return '';
    const start = toLocalDate(dateInput, tz);
    if (p === 'day') {
      return start.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
    }
    if (p === 'week') {
      const { week, year } = getISOWeek(start);
      return `Week ${week} ${year}`;
    }
    // month
    return start.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  }

  function getISOWeek(date) {
    // Copy date so don't modify original
    const d = new Date(date.getTime());
    // ISO week date weeks start on Monday, so correct the day number
    const dayNr = (d.getDay() + 6) % 7; // 0 = Monday, 6 = Sunday
    // Set the target to the Thursday of this week so the week number is correct near year boundaries
    d.setDate(d.getDate() - dayNr + 3);
    // January 4th is always in week 1 according to ISO
    const firstThursday = new Date(d.getFullYear(), 0, 4);
    const firstDayNr = (firstThursday.getDay() + 6) % 7;
    firstThursday.setDate(firstThursday.getDate() - firstDayNr + 3);
    // Calculate week number
    const week = 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
    const year = d.getFullYear();
    return { week, year };
  }

  function daysInPeriod(p, dateInput, tz) {
    const start = toLocalDate(dateInput, tz);
    if (p === 'day') return 1;
    if (p === 'week') return 7;
    // month
    const tmp = new Date(start);
    tmp.setMonth(tmp.getMonth() + 1, 0);
    return tmp.getDate();
  }

  import { studyProperties } from '$stores/configStore';
  function computeCompliance(count, p, dateInput, tz) {
    const days = daysInPeriod(p, dateInput, tz);
    const perDay = Math.round(1440 / (($studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5)));
    const expected = Math.max(days * perDay, 1);
    const pct = Math.min(100, (Number(count || 0) / expected) * 100);
    return `${pct.toFixed(1)}%`;
  }

  function pad(n) { return n.toString().padStart(2, '0'); }

  function formatMDYUTC(d) {
    return `${pad(d.getUTCMonth() + 1)}/${pad(d.getUTCDate())}/${d.getUTCFullYear()}`;
  }

  function formatPeriodRangeLocal(p, startInput) {
    if (!startInput) return '';
    const base = new Date(startInput);
    const year = base.getUTCFullYear();
    const month = base.getUTCMonth();
    const day = base.getUTCDate();

    let startUTC;
    let endUTC;

    if (p === 'day') {
      startUTC = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      endUTC = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
    } else if (p === 'week') {
      startUTC = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      endUTC = new Date(Date.UTC(year, month, day + 6, 23, 59, 59, 999));
    } else {
      // month
      startUTC = new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));
      // Day 0 of next month is last day of current month
      const lastDay = new Date(Date.UTC(year, month + 1, 0));
      endUTC = new Date(Date.UTC(lastDay.getUTCFullYear(), lastDay.getUTCMonth(), lastDay.getUTCDate(), 23, 59, 59, 999));
    }

    const s = `${formatMDYUTC(startUTC)}, 12:00:00 AM`;
    const e = `${formatMDYUTC(endUTC)}, 11:59:59 PM`;
    return `${s}\n${e}`;
  }
</script>

<StudyHeader />

<FeatureInfo 
  title="CGM Reading Counts" 
  description="Counts of CGM readings grouped by day, week, or month for the selected participant or all participants in the study/site."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{:else if $selectedSite}
  <SiteDetails showOptionsMenu={false} />
{:else if $selectedStudy}
  <StudyDetails showOptionsMenu={false} />
{/if}

<Toolbar title="CGM Reading Counts" itemCountTitle="Rows" itemCount={resultCount}>
  {#if !$selectedParticipant}
    <div class="flex items-baseline gap-2">
      Status:
      <select
        bind:value={selectedType}
        on:change={() => fetchCounts(1)}
        class="form-select h-6 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        <option value="ALL" selected>All</option>
        <option value="COMPD">Completed</option>
        <option value="CONS">Consented</option>
        <option value="DISC">Discontinued</option>
        <option value="INIT">Initiated</option>
        <option value="SCFA">Screen Failed</option>
        <option value="SCRE">Screened</option>
        <option value="ENRO">Screened/Enrolled</option>
      </select>
    </div>
  {/if}
  <div class="flex items-baseline gap-2">
    Period:
    <select
      bind:value={period}
      on:change={() => fetchCounts(1)}
      class="form-select w-32 h-6 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
    >
      <option value="day">Day</option>
      <option value="week">Week</option>
      <option value="month" selected>Month</option>
    </select>
  </div>
  <div class="flex items-center mx-2 print:hidden">
    <input
      type="checkbox"
      bind:checked={inRange}
      on:change={() => handleRefresh()}
      class="w-4 h-6 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
    />
    <span class="ms-2">Valid Device Range</span>
  </div>
  <div class="flex items-baseline">
    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
      preparing cgm counts report
    </p>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Site</th>
          <th class="px-4 py-2 text-left bold">Subject</th>
          <th class="px-4 py-2 text-left bold">Status</th>
          <th class="px-4 py-2 text-left bold">{period === 'day' ? 'Day' : period === 'week' ? 'Week' : 'Month'}</th>
          <th class="px-4 py-2 text-left bold">Period (Local)</th>
          <th class="px-4 py-2 text-left bold">Readings From - To (Local)</th>
          <th class="px-4 py-2 text-left bold">Count</th>
          <th class="px-4 py-2 text-left bold">Compliance %</th>
        </tr>
      </thead>
      <tbody>
        {#each rows as r, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{r.siteId ?? ''}</td>
            <td class="px-4 py-2">{r.subjectId ?? ''}</td>
            <td class="px-4 py-2">{r.subjectStatus ?? ''}</td>
            <td class="px-4 py-2">{formatPeriodLabel(period, r.periodStart, r.timezone)}</td>
            <td class="px-4 py-2 whitespace-pre-line">{formatPeriodRangeLocal(period, r.periodStart, r.timezone)}</td>
            <td class="px-4 py-2 w-52">
              {r.minRead ? new Date(r.minRead).toLocaleString('en-US', { timeZone: r.timezone || 'UTC' }) : ''}
              <br />
              {r.maxRead ? new Date(r.maxRead).toLocaleString('en-US', { timeZone: r.timezone || 'UTC' }) : ''}
            </td>
            <td class="px-4 py-2">{r.count}</td>
            <td class="px-4 py-2">{computeCompliance(r.count, period, r.periodStart, r.timezone)}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button on:click={handleExportExcel} class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs">Excel</button>
  </div>
</Footer>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
</style>

